// import { renderDate, renderTime } from "@intrasync/utils";
import { renderDate } from '@intrasync/utils';
import { renderTime } from '@intrasync/utils';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

import React from 'react';

import Button from '../../Button/Button';
import DatePicker from '../../DatePicker/DatePicker';
import FlexRow from '../../Flex/FlexRow';
import Input from '../../Input/Input';
import Select from '../../Select/Select';
import SelectItem from '../../Select/SelectItem';
import TimePicker from '../../TimePicker/TimePicker';
import injectStores from '../../RootStore/injectStores';
import { DataGridStore } from 'stores/index';
import RootStore from 'stores/RootStore';

interface DataGridBodyEditCellProps {
  cellDataType: string;
  cellEditValue: string;
  cellHeight: number;
  cellWidth: number;
  cellActualValue: string;
  onSubmitEdits: (
    cellUniqueKey: string,
    editData: { [key: string]: unknown },
    rootStore?: RootStore
  ) => void;
  cellUniqueKey: string;
  cellKey: string;
  cellEditKey: string; // Add the cellEditKey property
  cellHeader: {
    choices?: string[] | string;
    key?: string;
    dataType?: string;
    disableEdit?: boolean;
    width?: number;
  };
  namespaceId: string;
  dataGridStore?: DataGridStore;
  rootStore?: RootStore;
}

class DataGridBodyEditCell extends React.PureComponent<DataGridBodyEditCellProps> {
  // constructor(props: DataGridBodyEditCellProps) {
  //   super(props);
  //   makeObservable(this, {
  //     columnChoices: computed,
  //     currentNamespaceStore: computed,
  //   });
  // }

  setEditValue = (value: string | boolean | Date) => {
    const { cellUniqueKey, cellEditKey } = this.props;

    if (cellUniqueKey && this.currentNamespaceStore) {
      this.currentNamespaceStore.setEditData(cellUniqueKey, {
        [cellEditKey]: value,
      });
    }
  };

  cancelEdit = () => {
    const { cellUniqueKey, cellEditKey } = this.props;
    if (cellUniqueKey && this.currentNamespaceStore) {
      this.currentNamespaceStore.removeEditData(cellUniqueKey, cellEditKey);
    }
  };

  get columnChoices(): string[] {
    const { cellHeader } = this.props;
    if (!cellHeader?.choices) {
      return [];
    }
    if (typeof cellHeader.choices === 'string') {
      return (this.currentNamespaceStore?.currentStore[cellHeader.choices] as string[]) || [];
    }
    return cellHeader.choices || [];
  }

  get currentNamespaceStore() {
    const { dataGridStore, namespaceId } = this.props;
    return dataGridStore ? dataGridStore.namespaces[namespaceId] : undefined;
  }

  // Helper to ensure rootStore has the correct type
  getSafeRootStore(rootStore: RootStore) {
    return rootStore as unknown as import('stores/RootStore').default;
  }

  render() {
    const {
      cellDataType,
      cellEditValue,
      cellHeight,
      cellWidth,
      cellActualValue,
      onSubmitEdits,
      cellUniqueKey,
      cellKey,
      rootStore,
    } = this.props;
    const hasBeenEdited = cellActualValue !== cellEditValue;
    if (this.columnChoices?.length > 0) {
      return (
        <FlexRow
          style={{ height: cellHeight, width: cellWidth, alignItems: 'center' }}
        >
          <Select
            onChange={(value: string) => {
              this.setEditValue(value);
            }}
            value={cellEditValue}
            label={cellActualValue}
          >
            {this.columnChoices.map((choice: string) => {
              return (
                <SelectItem
                  key={choice}
                  value={choice}
                  style={{ minHeight: 40 }}
                />
              );
            })}
          </Select>
          <Button
            size="small"
            iconButton
            color="danger"
            onClick={() => this.cancelEdit()}
          >
            <CloseIcon />
          </Button>
          <Button
            size="small"
            iconButton
            color={hasBeenEdited ? 'success' : 'gray'}
            onClick={() => {
              if (hasBeenEdited) {
                onSubmitEdits(
                  cellUniqueKey,
                  {
                    [cellKey]: cellEditValue,
                  },
                  this.getSafeRootStore(rootStore)
                );
                this.cancelEdit();
              }
            }}
          >
            <SaveIcon />
          </Button>
        </FlexRow>
      );
    }
    if (cellDataType === 'boolean') {
      return (
        <FlexRow
          style={{ height: cellHeight, width: cellWidth, alignItems: 'center' }}
        >
          <Select
            onChange={(value: string) => {
              this.setEditValue(value);
            }}
            value={cellEditValue}
            label={cellActualValue}
          >
            <SelectItem key="True" value="True" />
            <SelectItem key="False" value="False" />
          </Select>
          <Button
            size="small"
            iconButton
            color="danger"
            onClick={() => this.cancelEdit()}
          >
            <CloseIcon />
          </Button>
          <Button
            size="small"
            iconButton
            color={hasBeenEdited ? 'success' : 'gray'}
            onClick={() => {
              if (hasBeenEdited) {
                onSubmitEdits(
                  cellUniqueKey,
                  {
                    [cellKey]: cellEditValue === 'True' ? true : false,
                  },
                  this.getSafeRootStore(rootStore)
                );
                this.cancelEdit();
              }
            }}
          >
            <SaveIcon />
          </Button>
        </FlexRow>
      );
    }
    if (cellDataType === 'date') {
      return (
        <FlexRow
          style={{ height: cellHeight, width: cellWidth, alignItems: 'center' }}
        >
          <DatePicker
            label={renderDate(cellActualValue)}
            value={cellEditValue}
            showClearButton
            onChange={(value) => this.setEditValue(value)}
          />
          <Button
            size="small"
            iconButton
            color="danger"
            onClick={() => this.cancelEdit()}
          >
            <CloseIcon />
          </Button>
          <Button
            size="small"
            iconButton
            color={hasBeenEdited ? 'success' : 'gray'}
            onClick={() => {
              if (hasBeenEdited) {
                onSubmitEdits(
                  cellUniqueKey,
                  {
                    [cellKey]: cellEditValue,
                  },
                  this.getSafeRootStore(rootStore)
                );
                this.cancelEdit();
              }
            }}
          >
            <SaveIcon />
          </Button>
        </FlexRow>
      );
    }

    if (cellDataType === 'time') {
      return (
        <FlexRow
          style={{ height: cellHeight, width: cellWidth, alignItems: 'center' }}
        >
          <TimePicker
            label={renderTime(cellActualValue)}
            value={cellEditValue}
            showClearButton
            onChange={(value) => this.setEditValue(value)}
          />
          <Button
            size="small"
            iconButton
            color="danger"
            onClick={() => this.cancelEdit()}
          >
            <CloseIcon />
          </Button>
          <Button
            size="small"
            iconButton
            color={hasBeenEdited ? 'success' : 'gray'}
            onClick={() => {
              if (hasBeenEdited) {
                onSubmitEdits(
                  cellUniqueKey,
                  {
                    [cellKey]: cellEditValue,
                  },
                  this.getSafeRootStore(rootStore)
                );
                this.cancelEdit();
              }
            }}
          >
            <SaveIcon />
          </Button>
        </FlexRow>
      );
    }

    return (
      <FlexRow
        style={{ height: cellHeight, width: cellWidth, alignItems: 'center' }}
      >
        <Input
          labelText={cellActualValue}
          value={cellEditValue}
          onChange={(value: string) => this.setEditValue(value)}
        />
        <Button
          size="small"
          iconButton
          color="danger"
          onClick={() => this.cancelEdit()}
        >
          <CloseIcon />
        </Button>
        <Button
          size="small"
          iconButton
          color={hasBeenEdited ? 'success' : 'gray'}
          onClick={() => {
            if (hasBeenEdited) {
              onSubmitEdits(
                cellUniqueKey,
                {
                  [cellKey]: cellEditValue,
                },
                this.getSafeRootStore(rootStore)
              );
              this.cancelEdit();
            }
          }}
        >
          <SaveIcon />
        </Button>
      </FlexRow>
    );
  }
}

const EnhancedDataGridBodyEditCell = injectStores(DataGridBodyEditCell);
export default EnhancedDataGridBodyEditCell;
